const EMPTY_ENVELOPE = Object.freeze({
  metadata: {},
  confidence: {
    label: "unknown",
    reasons: [],
    missingFields: [],
    incompleteMonths: [],
    blockers: [],
  },
  summaryCards: [],
  tables: [],
  charts: [],
  warnings: [],
  hasEnvelope: false,
});

const KNOWN_ENVELOPE_KEYS = [
  "metadata",
  "confidence",
  "summaryCards",
  "tables",
  "charts",
  "warnings",
];

export const toTitleCase = (value = "") =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const hasEnvelopeKeys = (value) =>
  isObject(value) && KNOWN_ENVELOPE_KEYS.some((key) => Object.prototype.hasOwnProperty.call(value, key));

export const resolveAnnualDataQualityEnvelopeSource = (data) => {
  if (hasEnvelopeKeys(data)) return data;
  if (Array.isArray(data)) {
    return data.find((item) => hasEnvelopeKeys(item)) || data[0] || null;
  }
  return null;
};

export const normalizeValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not available";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
  if (isObject(value)) return JSON.stringify(value);
  return String(value);
};

export const normalizeColumns = (columns = [], rows = []) => {
  const sourceColumns = Array.isArray(columns) && columns.length
    ? columns
    : Object.keys(rows[0] || {});

  return sourceColumns.map((column) => {
    if (typeof column === "string") {
      return { id: column, label: toTitleCase(column) };
    }

    return {
      id: column.id || column.key || column.accessor || column.name,
      label: column.label || column.title || toTitleCase(column.id || column.key || column.name),
    };
  }).filter((column) => column.id);
};

export const normalizeNamedCollection = (collection = {}, fallbackName) => {
  if (Array.isArray(collection)) {
    return collection.map((item, index) => ({
      key: item.key || item.id || item.name || item.title || `${fallbackName}-${index}`,
      title: item.title || item.name || toTitleCase(item.key || item.id || `${fallbackName} ${index + 1}`),
      ...item,
    }));
  }

  if (!isObject(collection)) return [];

  return Object.entries(collection).map(([key, item]) => {
    const source = isObject(item) ? item : { value: item };
    return {
      key,
      title: source.title || source.name || toTitleCase(key),
      ...source,
    };
  });
};

export const normalizeSummaryCards = (summaryCards = []) =>
  normalizeNamedCollection(summaryCards, "summary-card").map((card) => ({
    key: card.key,
    label: card.label || card.title,
    value: normalizeValue(card.value ?? card.count ?? card.total),
    unit: card.unit || "",
    change: card.change || "",
    status: card.status || card.confidence || "",
  }));

export const normalizeWarnings = (warnings = []) => {
  if (!Array.isArray(warnings)) return [];

  return warnings.map((warning, index) => {
    if (typeof warning === "string") {
      return {
        key: `warning-${index}`,
        severity: "warning",
        message: warning,
      };
    }

    return {
      key: warning.key || warning.id || `warning-${index}`,
      severity: warning.severity || warning.status || "warning",
      message: warning.message || warning.title || warning.description || normalizeValue(warning),
      scope: warning.scope || warning.area || "",
    };
  });
};

export const normalizeAnnualDataQualityEnvelope = (data) => {
  const source = resolveAnnualDataQualityEnvelopeSource(data);
  if (!source) return EMPTY_ENVELOPE;

  const confidence = isObject(source.confidence) ? source.confidence : {};
  const tables = normalizeNamedCollection(source.tables, "table").map((table) => {
    const rows = Array.isArray(table.rows) ? table.rows : [];
    return {
      ...table,
      columns: normalizeColumns(table.columns, rows),
      rows,
    };
  });

  const charts = normalizeNamedCollection(source.charts, "chart").map((chart) => ({
    ...chart,
    type: chart.type || "bar",
    data: Array.isArray(chart.data) ? chart.data : [],
  }));

  return {
    metadata: isObject(source.metadata) ? source.metadata : {},
    confidence: {
      label: confidence.label || "unknown",
      score: confidence.score,
      reasons: Array.isArray(confidence.reasons) ? confidence.reasons : [],
      missingFields: Array.isArray(confidence.missingFields) ? confidence.missingFields : [],
      incompleteMonths: Array.isArray(confidence.incompleteMonths) ? confidence.incompleteMonths : [],
      blockers: Array.isArray(confidence.blockers) ? confidence.blockers : [],
    },
    summaryCards: normalizeSummaryCards(source.summaryCards),
    tables,
    charts,
    warnings: normalizeWarnings(source.warnings),
    hasEnvelope: true,
  };
};

